/* KEORA v2 : comportements du theme. Composants HTML personnalises (custom elements),
   relances automatiquement par l'editeur de theme quand une section est rechargee.
   Aucune dependance externe. Respecte prefers-reduced-motion. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };
  const decodeEntities = (str) => { const t = document.createElement('textarea'); t.innerHTML = str; return t.value; };
  const focusables = (root) => [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])')].filter((el) => !el.closest('[hidden]'));

  /* ---------- En-tete : panneaux du menu et menu telephone ---------- */
  class KHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[data-k-drawer]');
      this.scroller = this.querySelector('.k-drawer__scroll');
      this.panels = [...this.querySelectorAll('[data-k-panel]')];
      this.burger = this.querySelector('[data-k-burger]');
      this.burgerLabel = this.querySelector('[data-k-burger-label]');
      this.back = this.querySelector('[data-k-back]');
      this.veil = this.querySelector('[data-k-veil]');
      this.desktop = window.matchMedia('(min-width: 1024px)');
      this.current = null;

      this.querySelectorAll('[data-k-open]').forEach((btn) => {
        btn.addEventListener('click', (event) => {
          const id = btn.dataset.kOpen;
          if (this.current === id && this.desktop.matches) { this.close(); return; }
          this.lastTrigger = btn;
          this.open(id, event.detail === 0);
        });
      });
      this.burger?.addEventListener('click', () => {
        if (this.classList.contains('is-open')) this.close();
        else { this.lastTrigger = this.burger; this.open('root'); }
      });
      this.back?.addEventListener('click', () => this.open('root', true));
      this.veil?.addEventListener('click', () => this.close());

      this.onKey = (event) => {
        if (event.key === 'Escape' && this.classList.contains('is-open')) {
          this.close();
          this.lastTrigger?.focus();
        }
      };
      this.onDocClick = (event) => {
        if (this.classList.contains('is-open') && !this.contains(event.target)) this.close();
      };
      this.onResize = () => { if (this.classList.contains('is-open') && this.desktop.matches) this.fit(); };
      /* Comme keora.co : pastille visible des qu'on a defile, en-tete masque en descendant et rappele en remontant. */
      this.section = this.closest('.shopify-section') || this;
      this.lastY = window.scrollY;
      this.onScroll = () => {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          const y = Math.max(0, window.scrollY);
          this.classList.toggle('is-scrolled', y > 10);
          if (this.dataset.sticky === 'true' && !this.classList.contains('is-open')) {
            if (y > this.lastY + 4 && y > 200) this.section.classList.add('k-header-hidden');
            else if (y < this.lastY - 4 || y <= 200) this.section.classList.remove('k-header-hidden');
          }
          this.lastY = y;
          this.ticking = false;
        });
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.onBreakpoint = () => this.close(true);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('click', this.onDocClick);
      window.addEventListener('resize', this.onResize, { passive: true });
      this.desktop.addEventListener('change', this.onBreakpoint);
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('click', this.onDocClick);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('scroll', this.onScroll);
      this.desktop.removeEventListener('change', this.onBreakpoint);
      document.documentElement.classList.remove('k-lock');
    }

    open(id, moveFocus = false) {
      this.current = id;
      this.panels.forEach((panel) => { panel.hidden = panel.dataset.kPanel !== id; });
      this.querySelectorAll('[data-k-open]').forEach((btn) => btn.setAttribute('aria-expanded', String(btn.dataset.kOpen === id)));
      this.classList.add('is-open');
      this.classList.toggle('is-sub', id !== 'root');
      this.burger?.setAttribute('aria-expanded', 'true');
      if (this.burgerLabel) this.burgerLabel.textContent = this.burgerLabel.dataset.close;
      if (!this.desktop.matches) document.documentElement.classList.add('k-lock');
      this.fit();
      if (moveFocus) {
        const panel = this.panels.find((p) => p.dataset.kPanel === id);
        requestAnimationFrame(() => focusables(panel || this)[0]?.focus());
      }
    }

    fit() {
      if (!this.drawer) return;
      if (this.desktop.matches) {
        this.drawer.style.height = 'auto';
        const h = this.drawer.scrollHeight;
        this.drawer.style.height = '0px';
        requestAnimationFrame(() => { this.drawer.style.height = `${h}px`; });
      } else {
        this.drawer.style.height = '100svh';
      }
    }

    close(instant = false) {
      this.classList.remove('is-open', 'is-sub');
      this.querySelectorAll('[data-k-open]').forEach((btn) => btn.setAttribute('aria-expanded', 'false'));
      this.burger?.setAttribute('aria-expanded', 'false');
      if (this.burgerLabel) this.burgerLabel.textContent = this.burgerLabel.dataset.open;
      document.documentElement.classList.remove('k-lock');
      if (!this.drawer) return;
      this.drawer.style.height = '0px';
      const done = () => { if (!this.classList.contains('is-open')) this.panels.forEach((p) => { p.hidden = true; }); };
      if (instant || reduceMotion.matches) done(); else setTimeout(done, 460);
      this.current = null;
    }
  }
  define('k-header', KHeader);


  /* ---------- Carrousels : defilement aimante natif, fleches, barre de progression, compteur ---------- */
  class KCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-k-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-k-prev]');
      this.next = this.querySelector('[data-k-next]');
      this.bar = this.querySelector('[data-k-progress]');
      this.counter = this.querySelector('[data-k-counter]');
      this.dots = [...this.querySelectorAll('[data-k-dot]')];
      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.onScroll = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); }); };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.update();
    }
    disconnectedCallback() { window.removeEventListener('resize', this.onScroll); }
    items() { return [...this.track.children].filter((el) => el.offsetParent !== null); }
    step() {
      const items = this.items();
      if (items.length < 2) return this.track.clientWidth;
      return items[1].offsetLeft - items[0].offsetLeft;
    }
    go(dir) { this.track.scrollBy({ left: dir * this.step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }); }
    goTo(i) {
      const items = this.items();
      if (!items[i]) return;
      this.track.scrollTo({ left: items[i].offsetLeft - items[0].offsetLeft, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    index() {
      const items = this.items();
      const left = this.track.scrollLeft + 2;
      let idx = 0;
      items.forEach((el, i) => { if (el.offsetLeft - items[0].offsetLeft <= left) idx = i; });
      if (this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2) idx = items.length - 1;
      return idx;
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const ratio = max > 0 ? Math.min(1, Math.max(0, this.track.scrollLeft / max)) : 1;
      if (this.bar) this.bar.style.transform = `scaleX(${ratio})`;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max - 2;
      const i = this.index();
      if (this.counter) this.counter.textContent = `${i + 1} / ${this.items().length}`;
      this.dots.forEach((dot, n) => { if (n === i) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current'); });
    }
  }
  define('k-carousel', KCarousel);


  /* ---------- Onglets : role tab sur ordinateur, panneaux empiles sur telephone ---------- */
  class KTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[role="tab"]')];
      this.panels = [...this.querySelectorAll('[data-k-panel]')];
      this.desktop = window.matchMedia('(min-width: 1024px)');
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (event) => {
          const keys = { ArrowRight: 1, ArrowLeft: -1, Home: 'first', End: 'last' };
          if (!(event.key in keys)) return;
          event.preventDefault();
          let n = keys[event.key] === 'first' ? 0 : keys[event.key] === 'last' ? this.tabs.length - 1 : (i + keys[event.key] + this.tabs.length) % this.tabs.length;
          this.select(n, true);
        });
      });
      this.onBp = () => this.applyMode();
      this.desktop.addEventListener('change', this.onBp);
      this.applyMode();
    }
    disconnectedCallback() { this.desktop.removeEventListener('change', this.onBp); }
    applyMode() {
      const d = this.desktop.matches;
      this.panels.forEach((p) => {
        if (d) { p.setAttribute('role', 'tabpanel'); p.setAttribute('tabindex', '0'); }
        else { p.removeAttribute('role'); p.removeAttribute('tabindex'); }
      });
    }
    select(i, focus = false) {
      this.tabs.forEach((tab, n) => {
        const on = n === i;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach((p, n) => p.classList.toggle('is-active', n === i));
      if (focus) this.tabs[i].focus();
    }
  }
  define('k-tabs', KTabs);


  /* ---------- Onglets synchronises : plusieurs listes d'onglets (telephone, ordinateur) pilotent les memes panneaux ---------- */
  class KSwitch extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-k-tab]')];
      this.panes = [...this.querySelectorAll('[data-k-pane]')];
      this.tabs.forEach((tab) => {
        tab.addEventListener('click', () => this.select(tab.dataset.kTab));
        tab.addEventListener('keydown', (event) => {
          const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
          if (!step) return;
          event.preventDefault();
          const list = [...tab.parentElement.querySelectorAll('[data-k-tab]')];
          const next = list[(list.indexOf(tab) + step + list.length) % list.length];
          this.select(next.dataset.kTab);
          next.focus();
        });
      });
    }
    select(value) {
      this.tabs.forEach((tab) => {
        const on = tab.dataset.kTab === value;
        tab.setAttribute('aria-selected', String(on));
        tab.tabIndex = on ? 0 : -1;
      });
      this.panes.forEach((pane) => pane.classList.toggle('is-active', pane.dataset.kPane === value));
    }
  }
  define('k-switch', KSwitch);


  /* ---------- Fondu enchaine : photos qui se succedent (aucun mouvement si l'utilisateur le demande) ---------- */
  class KFondu extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.children];
      if (this.slides.length < 2 || reduceMotion.matches) return;
      this.i = 0;
      this.timer = setInterval(() => {
        this.slides[this.i].classList.remove('is-active');
        this.i = (this.i + 1) % this.slides.length;
        this.slides[this.i].classList.add('is-active');
      }, parseInt(this.dataset.interval, 10) || 4000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
  }
  define('k-fondu', KFondu);


  /* ---------- Fenetre video : fichier mp4 de la boutique, YouTube ou Vimeo ---------- */
  const videoModal = () => document.getElementById('KVideoModal');
  const closeVideo = () => {
    const dlg = videoModal();
    if (!dlg) return;
    const media = dlg.querySelector('[data-k-video-media]');
    if (media) media.innerHTML = '';
    if (dlg.open) dlg.close();
    document.documentElement.classList.remove('k-video-open');
  };
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-k-video]');
    if (trigger) {
      const dlg = videoModal();
      if (!dlg || typeof dlg.showModal !== 'function') return;
      event.preventDefault();
      const media = dlg.querySelector('[data-k-video-media]');
      const kind = trigger.dataset.kVideo;
      if (kind === 'mp4') {
        media.innerHTML = '<video autoplay muted playsinline controls preload="auto"></video>';
        const video = media.querySelector('video');
        video.src = trigger.dataset.kVideoSrc;
        const play = video.play();
        if (play && play.catch) play.catch(() => {});
      } else if (kind === 'youtube' || kind === 'vimeo') {
        const id = encodeURIComponent(trigger.dataset.kVideoId || '');
        const src = kind === 'youtube' ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0` : `https://player.vimeo.com/video/${id}?autoplay=1`;
        media.innerHTML = `<iframe src="${src}" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen title="${trigger.textContent.trim().replace(/"/g, '')}"></iframe>`;
      } else {
        return;
      }
      dlg.showModal();
      document.documentElement.classList.add('k-video-open');
      return;
    }
    if (event.target.closest('[data-k-video-close]')) { closeVideo(); return; }
    const dlg = videoModal();
    if (dlg && event.target === dlg) closeVideo();
  });
  document.addEventListener('close', (event) => { if (event.target && event.target.id === 'KVideoModal') closeVideo(); }, true);


  /* ---------- Fonctionnalites : titre actif, ecran correspondant, compteur circulaire ---------- */
  class KFeatures extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-k-feat]')];
      this.descs = [...this.querySelectorAll('.k-feat-desc')];
      this.slides = [...this.querySelectorAll('[data-k-slide]')];
      this.ring = this.querySelector('[data-k-ring]');
      this.count = this.querySelector('[data-k-count]');
      this.prev = this.querySelector('[data-k-feat-prev]');
      this.next = this.querySelector('[data-k-feat-next]');
      this.i = 0;
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.show(i)));
      this.prev?.addEventListener('click', () => this.show(this.i - 1));
      this.next?.addEventListener('click', () => this.show(this.i + 1));
      this.show(0);
    }
    show(i) {
      const n = this.slides.length;
      if (!n) return;
      this.i = Math.max(0, Math.min(n - 1, i));
      this.tabs.forEach((tab, k) => { tab.setAttribute('aria-expanded', String(k === this.i)); tab.classList.toggle('is-active', k === this.i); });
      this.descs.forEach((d, k) => { d.hidden = k !== this.i; });
      this.slides.forEach((sl, k) => { sl.hidden = k !== this.i; });
      if (this.ring) this.ring.setAttribute('stroke-dashoffset', String(66 * (1 - (this.i + 1) / n)));
      if (this.count) this.count.textContent = `${this.i + 1}/${n}`;
      if (this.prev) this.prev.disabled = this.i === 0;
      if (this.next) this.next.disabled = this.i === n - 1;
    }
  }
  define('k-features', KFeatures);


  /* ---------- Temoignages : la carte citation se place contre la photo choisie ---------- */
  class KTestimonials extends HTMLElement {
    connectedCallback() {
      this.card = this.querySelector('[data-k-testi-card]');
      this.contents = [...this.querySelectorAll('[data-k-testi-content]')];
      this.photos = [...this.querySelectorAll('[data-k-testi]')];
      this.photos.forEach((btn, i) => btn.addEventListener('click', () => this.show(i)));
    }
    show(i) {
      if (!this.card) return;
      this.card.style.order = String(i * 2 + 1);
      this.contents.forEach((c, k) => { c.classList.toggle('hidden', k !== i); c.classList.toggle('flex', k === i); });
      this.photos.forEach((p, k) => p.setAttribute('aria-pressed', String(k === i)));
      if (!reduceMotion.matches) this.card.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 400, easing: 'ease-out' });
    }
  }
  define('k-testimonials', KTestimonials);

  /* ---------- Apparition au defilement ---------- */
  const revealObserver = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
    }), { rootMargin: '0px 0px -10% 0px' })
    : null;
  const watchReveals = (root = document) => root.querySelectorAll('.k-reveal:not(.is-visible)').forEach((el) => {
    if (revealObserver && !reduceMotion.matches) revealObserver.observe(el); else el.classList.add('is-visible');
  });
  document.addEventListener('DOMContentLoaded', () => watchReveals());
  document.addEventListener('shopify:section:load', (event) => watchReveals(event.target));



  /* ---------- Zones d'ambiance : un seul degrade continu sur plusieurs sections qui se suivent ---------- */
  const layoutZones = () => {
    const sections = [...document.querySelectorAll('#MainContent [data-k-zone]')];
    let run = [];
    const flush = () => {
      if (!run.length) return;
      const top = run[0].getBoundingClientRect().top + window.scrollY;
      const last = run[run.length - 1];
      const bottom = last.getBoundingClientRect().bottom + window.scrollY;
      const total = Math.max(1, bottom - top);
      run.forEach((el) => {
        el.style.setProperty('--k-zone-h', `${total}px`);
        el.style.setProperty('--k-zone-y', `${el.getBoundingClientRect().top + window.scrollY - top}px`);
      });
      run = [];
    };
    sections.forEach((el, i) => {
      const prev = sections[i - 1];
      const contiguous = prev && prev.dataset.kZone === el.dataset.kZone
        && Math.abs(prev.getBoundingClientRect().bottom - el.getBoundingClientRect().top) < 2;
      if (!contiguous) flush();
      run.push(el);
    });
    flush();
  };
  let zoneTimer;
  const scheduleZones = () => { clearTimeout(zoneTimer); zoneTimer = setTimeout(layoutZones, 60); };
  document.addEventListener('DOMContentLoaded', layoutZones);
  window.addEventListener('load', layoutZones);
  window.addEventListener('resize', scheduleZones, { passive: true });
  document.addEventListener('shopify:section:load', scheduleZones);
  document.addEventListener('shopify:section:unload', scheduleZones);
  document.addEventListener('shopify:section:reorder', scheduleZones);
  if ('ResizeObserver' in window) {
    const ro = new ResizeObserver(scheduleZones);
    document.addEventListener('DOMContentLoaded', () => { const main = document.getElementById('MainContent'); if (main) ro.observe(main); });
  }

  /* ---------- Panier : ajout sans rechargement, tiroir, quantites ---------- */
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const updateCartCount = (count) => {
    document.querySelectorAll('[data-k-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('hidden', !count);
    });
    document.querySelectorAll('[data-k-cart-label]').forEach((el) => {
      const tpl = count === 1 ? el.dataset.one : el.dataset.other;
      if (tpl) el.textContent = tpl.replace(/\d+/, count);
    });
  };

  class KCartDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.content = this.querySelector('#KCartDrawerContent');
      this.status = this.querySelector('[data-k-cart-status]');
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-k-cart-close]')) { event.preventDefault(); this.close(); return; }
        const qty = event.target.closest('[data-k-qty]');
        if (qty) this.change(qty.closest('[data-k-line]').dataset.kLine, parseInt(qty.dataset.kQty, 10));
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-k-qty-input]');
        if (input) this.change(input.closest('[data-k-line]').dataset.kLine, Math.max(0, parseInt(input.value, 10) || 0));
      });
      this.onKey = (event) => {
        if (this.hidden) return;
        if (event.key === 'Escape') this.close();
        if (event.key === 'Tab') {
          const items = focusables(this.panel);
          if (!items.length) return;
          const first = items[0]; const last = items[items.length - 1];
          if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
          else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
        }
      };
      document.addEventListener('keydown', this.onKey);
    }

    disconnectedCallback() { document.removeEventListener('keydown', this.onKey); }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.hidden = false;
      document.documentElement.classList.add('k-lock');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-open')));
      setTimeout(() => this.panel.focus(), 60);
    }

    close() {
      this.classList.remove('is-open');
      document.documentElement.classList.remove('k-lock');
      setTimeout(() => { this.hidden = true; }, reduceMotion.matches ? 0 : 320);
      if (this.trigger && this.trigger.focus) this.trigger.focus();
    }

    render(html) {
      if (!html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector('[data-k-cart-root]');
      if (!fresh) return;
      this.content.replaceChildren(fresh);
      updateCartCount(parseInt(fresh.dataset.count, 10) || 0);
    }

    announce(text) { if (this.status) { this.status.textContent = ''; setTimeout(() => { this.status.textContent = text; }, 50); } }

    async change(line, quantity) {
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: ['cart-drawer'], sections_url: window.location.pathname }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message);
        this.render(json.sections && json.sections['cart-drawer']);
      } catch (error) {
        this.announce(error.message || this.dataset.error || '');
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  define('k-cart-drawer', KCartDrawer);


  /* ---------- Page panier : quantites sans rechargement (API Section Rendering) ---------- */
  class KCartPage extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const qty = event.target.closest('[data-k-qty]');
        if (!qty) return;
        event.preventDefault();
        this.change(qty.closest('[data-k-line]').dataset.kLine, parseInt(qty.dataset.kQty, 10));
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-k-qty-input]');
        if (input) this.change(input.closest('[data-k-line]').dataset.kLine, Math.max(0, parseInt(input.value, 10) || 0));
      });
    }
    async change(line, quantity) {
      const id = this.dataset.sectionId;
      this.setAttribute('aria-busy', 'true');
      try {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: [id], sections_url: window.location.pathname }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.description || json.message);
        const doc = new DOMParser().parseFromString(json.sections[id], 'text/html');
        const fresh = doc.querySelector('[data-k-cart-page-root]');
        const current = this.querySelector('[data-k-cart-page-root]');
        if (fresh && current) current.replaceWith(fresh);
        updateCartCount(json.item_count);
      } catch (error) {
        window.location.reload();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }
  }
  define('k-cart-page', KCartPage);

  /* Formulaires d'ajout marques data-k-ajax : ajout sans rechargement puis ouverture du tiroir. */
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-k-ajax')) return;
    const drawer = document.querySelector('k-cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-k-form-error]');
    if (button) { button.setAttribute('aria-disabled', 'true'); button.classList.add('is-busy'); }
    if (error) { error.hidden = true; error.textContent = ''; }
    const data = new FormData(form);
    data.append('sections', 'cart-drawer');
    data.append('sections_url', window.location.pathname);
    try {
      const res = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' }, body: data });
      const json = await res.json();
      if (!res.ok || json.status) throw new Error(json.description || json.message);
      drawer.render(json.sections && json.sections['cart-drawer']);
      drawer.open(button);
      if (json.product_title) drawer.announce(json.product_title);
    } catch (err) {
      if (error) { error.textContent = err.message || error.dataset.fallback || ''; error.hidden = false; }
    } finally {
      if (button) { button.removeAttribute('aria-disabled'); button.classList.remove('is-busy'); }
    }
  });

  /* L'icone panier ouvre le tiroir quand il existe. */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-k-cart-link]');
    const drawer = document.querySelector('k-cart-drawer');
    if (link && drawer) { event.preventDefault(); drawer.open(link); }
  });

  /* Selecteurs qui envoient leur formulaire au changement (langue). */
  document.addEventListener('change', (event) => {
    if (event.target.matches && event.target.matches('[data-k-autosubmit]') && event.target.form) event.target.form.submit();
  });

  /* ---------- Accordeons : details natifs, ouverture et fermeture en douceur ---------- */
  class KAccordion extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        const summary = event.target.closest('summary');
        if (!summary || summary.closest('k-accordion') !== this) return;
        const details = summary.parentElement;
        const panel = details.querySelector(':scope > .k-acc__panel');
        if (!panel || reduceMotion.matches || !panel.animate) return;
        event.preventDefault();
        if (details.dataset.animating) return;
        details.dataset.animating = '1';
        const done = () => { delete details.dataset.animating; };
        if (details.open) {
          const anim = panel.animate([{ height: `${panel.scrollHeight}px` }, { height: '0px' }], { duration: 260, easing: 'ease-in-out' });
          anim.onfinish = () => { details.open = false; done(); };
        } else {
          details.open = true;
          const anim = panel.animate([{ height: '0px' }, { height: `${panel.scrollHeight}px` }], { duration: 260, easing: 'ease-in-out' });
          anim.onfinish = done;
        }
      });
    }
  }
  define('k-accordion', KAccordion);

  /* ---------- Galerie produit : defilement aimante, pastilles de progression, filtre par finition ---------- */
  class KGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-k-track]');
      if (!this.track) return;
      this.pagers = this.querySelector('[data-k-pagers]');
      this.live = this.querySelector('[data-k-live]');
      this.querySelector('[data-k-prev]')?.addEventListener('click', () => this.go(this.index() - 1));
      this.querySelector('[data-k-next]')?.addEventListener('click', () => this.go(this.index() + 1));
      this.onScroll = () => { if (!this.raf) this.raf = requestAnimationFrame(() => { this.raf = null; this.update(); }); };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.build();
    }
    slides() { return [...this.track.querySelectorAll('[data-k-slide]')].filter((el) => !el.hidden); }
    filter(finish) {
      this.track.querySelectorAll('[data-k-slide]').forEach((el) => {
        const f = el.dataset.kFinish;
        el.hidden = !(f === 'all' || f === finish);
      });
      this.track.scrollTo({ left: 0, behavior: 'auto' });
      this.build();
    }
    build() {
      if (!this.pagers) return;
      const n = this.slides().length;
      this.pagers.replaceChildren();
      for (let i = 0; i < n; i += 1) {
        const li = document.createElement('li');
        li.className = 'leading-[0px]';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'flex items-center';
        btn.setAttribute('aria-label', (this.dataset.goto || '%i').replace('%i', i + 1));
        const pill = document.createElement('span');
        pill.className = 'k-pager';
        btn.append(pill);
        btn.addEventListener('click', () => this.go(i));
        li.append(btn);
        this.pagers.append(li);
      }
      this.update();
    }
    index() { const w = this.track.clientWidth || 1; return Math.max(0, Math.round(this.track.scrollLeft / w)); }
    go(i) {
      const n = this.slides().length;
      if (!n) return;
      const k = ((i % n) + n) % n;
      this.track.scrollTo({ left: k * this.track.clientWidth, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    }
    update() {
      const i = this.index();
      const n = this.slides().length;
      this.pagers?.querySelectorAll('button').forEach((b, k) => {
        b.firstChild.classList.toggle('is-current', k === i);
        if (k === i) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
      });
      if (this.live) this.live.textContent = (this.dataset.live || '%i / %n').replace('%i', i + 1).replace('%n', n);
    }
  }
  define('k-gallery', KGallery);

  /* ---------- Formulaire produit : options, variante, prix, galerie, barre d'achat fixe ---------- */
  class KProduct extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-k-variants]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.form = this.querySelector('form[data-k-product-form]');
      this.idInput = this.querySelector('[data-k-variant-id]');
      if (this.idInput) this.idInput.disabled = false;
      this.gallery = this.querySelector('k-gallery');
      this.kit = this.dataset.kit;
      this.sticky = this.querySelector('[data-k-sticky]');
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-k-option], [data-k-mode]')) this.onChange(event.target);
      });
      this.form?.addEventListener('submit', (event) => this.onSubmit(event));
      const mainAtc = this.form?.querySelector('[data-k-atc]');
      if (this.sticky && mainAtc && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(([entry]) => {
          const past = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          this.sticky.classList.toggle('is-visible', past);
        });
        this.io.observe(mainAtc);
      }
      this.finishPos = Number(this.querySelector('[data-k-finish-input]')?.dataset.kOption || 0);
      this.sync(false);
    }
    disconnectedCallback() { this.io?.disconnect(); }
    sizePos() {
      const input = this.querySelector('[data-k-size-grid] [data-k-option]');
      return input ? Number(input.dataset.kOption) : 0;
    }
    options() {
      const n = this.variants[0].options.length;
      const opts = new Array(n).fill(null);
      this.querySelectorAll('[data-k-option]:checked').forEach((input) => { opts[Number(input.dataset.kOption) - 1] = input.value; });
      const mode = this.querySelector('[data-k-mode]:checked');
      const pos = this.sizePos();
      if (mode && pos) {
        if (mode.value === 'kit') opts[pos - 1] = this.kit;
        else {
          const chosen = this.querySelector('[data-k-size-grid] [data-k-option]:checked');
          opts[pos - 1] = chosen ? chosen.value : null;
        }
      }
      return opts;
    }
    onChange(target) {
      if (target.matches('[data-k-mode]')) {
        this.querySelector('[data-k-size-grid]')?.classList.toggle('hidden', target.value !== 'size');
      }
      this.hint(false);
      this.sync(true);
    }
    sync(updateUrl) {
      const opts = this.options();
      const complete = opts.every((o) => o !== null);
      const match = (v) => v.options.every((o, i) => opts[i] === null || o === opts[i]);
      const v = complete ? this.variants.find((x) => x.options.every((o, i) => o === opts[i])) : this.variants.find(match);
      this.current = complete ? v || null : null;
      if (v) {
        this.querySelectorAll('[data-k-price]').forEach((el) => { el.textContent = decodeEntities(v.price); });
        this.querySelectorAll('[data-k-compare]').forEach((el) => { el.textContent = decodeEntities(v.compare || ''); el.hidden = !v.compare; });
      }
      const finish = this.finishPos ? opts[this.finishPos - 1] : null;
      if (finish) {
        this.querySelectorAll('[data-k-finish-label], [data-k-sticky-sub]').forEach((el) => { el.textContent = finish; });
        if (finish !== this.lastFinish && this.lastFinish !== undefined) this.gallery?.filter(finish);
        this.lastFinish = finish;
      }
      const available = this.current ? this.current.available : true;
      const isAccessory = this.dataset.style === 'accessoire';
      this.querySelectorAll('[data-k-atc]').forEach((btn) => {
        btn.disabled = isAccessory ? !(this.current && this.current.available) : !available;
        btn.classList.toggle('is-ready', !!(this.current && this.current.available));
      });
      this.querySelectorAll('[data-k-atc-label]').forEach((el) => { el.textContent = available ? el.dataset.add : el.dataset.sold; });
      if (this.current && this.idInput) {
        this.idInput.value = this.current.id;
        if (updateUrl) {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', this.current.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
      }
    }
    hint(show) {
      this.querySelectorAll('[data-k-hint]').forEach((el) => el.classList.toggle('hidden', !show));
    }
    onSubmit(event) {
      if (!this.current) {
        event.preventDefault();
        event.stopPropagation();
        this.hint(true);
        const target = this.querySelector('[data-k-size-grid]:not(.hidden)') || this.querySelector('[role="radiogroup"]');
        target?.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        (this.querySelector('[data-k-size-grid] input') || this.querySelector('[role="radiogroup"] input'))?.focus({ preventScroll: true });
      }
    }
  }
  define('k-product', KProduct);

  /* ---------- Choix de formule (Pack) : libelle du bouton et garde avant l'ajout ---------- */
  document.addEventListener('change', (event) => {
    const input = event.target.closest && event.target.closest('[data-k-formule]');
    if (!input) return;
    const form = input.form;
    const btn = form && form.querySelector('[data-k-formule-atc]');
    if (!btn) return;
    btn.textContent = decodeEntities((btn.dataset.template || '%f').replace('%f', input.dataset.name).replace('%p', input.dataset.price));
    btn.setAttribute('aria-disabled', 'false');
    form.querySelectorAll('[data-k-hint]').forEach((el) => el.classList.add('hidden'));
  });
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-k-formules')) return;
    if (form.querySelector('[data-k-formule]:checked')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.querySelectorAll('[data-k-hint]').forEach((el) => el.classList.remove('hidden'));
    form.querySelector('[data-k-formule]')?.focus();
  }, true);

  /* Exposes pour les autres modules du fichier */
  window.KEORA = Object.assign(window.KEORA || {}, { reduceMotion, define, focusables, watchReveals, updateCartCount });
})();
